import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Download,
  FileText as FileTextIcon,
  Smartphone,
  Pencil,
  Trash2,
  QrCode,
} from "lucide-react";
import { api, formatApiError, API, authQuery, downloadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";

function StatusBadge({ status, t }) {
  const map = {
    present: { label: t("attendance.present"), cls: "active" },
    absent: { label: t("attendance.absent"), cls: "excluded" },
    excused: { label: t("attendance.excused"), cls: "dormant" },
    confirmed: { label: t("attendance.confirmed", "Confirmat"), cls: "honorary" },
  };
  const c = map[status] || map.absent;
  return <span className={`badge-status ${c.cls}`} data-testid={`status-badge-${status || 'absent'}`}>{c.label}</span>;
}

function MethodBadge({ method, t }) {
  if (!method) return <span className="kv-label">—</span>;
  const isNfc = method === "nfc";
  const isMobile = method === "mobile_rsvp" || method === "mobile_rsvp_cancel";
  return (
    <span
      className="badge-grade"
      style={{
        fontSize: "0.6rem",
        background: isMobile ? "#fdf4e0" : isNfc ? "#fdf4e0" : "#fff",
        borderColor: (isNfc || isMobile) ? "#c9a84c" : "#cfc9b7",
        color: (isNfc || isMobile) ? "#9a7530" : "#6b6557",
      }}
    >
      {isMobile ? (
        <>
          <Smartphone size={9} style={{ display: "inline", marginRight: 3 }} />
          {t("attendance.methodMobile", "Aplicație mobilă")}
        </>
      ) : isNfc ? (
        <>
          <Smartphone size={9} style={{ display: "inline", marginRight: 3 }} />
          {t("attendance.methodNfc")}
        </>
      ) : (
        t("attendance.methodManual")
      )}
    </span>
  );
}

export default function AttendancePage() {
  const { t, i18n } = useTranslation();
  const locale =t("attendance.en_gb");
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  if (eventId) return <EventAttendanceDetail eventId={eventId} t={t} locale={locale} isAdmin={isAdmin} />;

  return <AttendanceOverview t={t} locale={locale} navigate={navigate} />;
}

function AttendanceOverview({ t, locale, navigate }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/attendance/overview");
      setItems(data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const removeEvent = async (ev) => {
    const ok = window.confirm(
      t("attendance.deleteHistoryConfirm", `Ștergi ședința "${ev.title}" și toate prezențele înregistrate? Acțiunea nu poate fi anulată.`)
    );
    if (!ok) return;
    try {
      await api.delete(`/events/${ev.id}`);
      toast.success(t("events.deletedSuccess", "Eveniment șters."));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="attendance-overview-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="page-title">{t("attendance.title")}</h1>
          <p className="page-subtitle">{t("attendance.subtitle")}</p>
        </div>
        <button
          type="button"
          className="btn-primary-gold flex items-center gap-2"
          onClick={() => navigate("/scan")}
          data-testid="attendance-scan-btn"
        >
          <Smartphone size={14} />
          {t("attendance.scanPassport", "SCANEAZĂ PAȘAPORT")}
        </button>
      </div>
      <div className="gold-divider my-8" />
      {error && <div className="error-banner mb-4">{error}</div>}
      {items.length === 0 ? (
        <p style={{ fontStyle: "italic", color: "#858481" }}>{t("attendance.noEvents")}</p>
      ) : (
        <table className="lodge-table" data-testid="attendance-events-table">
          <thead>
            <tr>
              <th>{t("events.eventDate")}</th>
              <th>{t("events.eventType", "")}</th>
              <th style={{ textAlign: "center" }}>{t("attendance.present")}</th>
              <th style={{ textAlign: "center" }}>{t("attendance.absent")}</th>
              <th style={{ textAlign: "center" }}>{t("attendance.excused")}</th>
              <th style={{ textAlign: "center" }}>{t("attendance.confirmed", "Confirmat")}</th>
              <th style={{ textAlign: "center" }}>{t("attendance.percentage")}</th>
              <th style={{ textAlign: "right" }}>{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.event.id}>
                <td>
                  <div style={{ fontFamily: "Cinzel, serif", color: "#0d1b2a", fontSize: "0.95rem" }}>{it.event.title}</div>
                  <div className="kv-label" style={{ fontSize: "0.62rem" }}>
                    {new Date(it.event.date).toLocaleString(locale, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </td>
                <td>{it.event.event_type || "—"}</td>
                <td style={{ textAlign: "center", color: "#3e6a3c", fontWeight: 600 }}>{it.summary.present}</td>
                <td style={{ textAlign: "center", color: "#7c2a2a" }}>{it.summary.absent}</td>
                <td style={{ textAlign: "center", color: "#9a7530" }}>{it.summary.excused}</td>
                <td style={{ textAlign: "center", color: "#9a7530", fontWeight: 600 }} data-testid={`attendance-confirmed-${it.event.id}`}>{it.summary.confirmed || 0}</td>
                <td style={{ textAlign: "center" }}>
                  <strong>{it.summary.percentage}%</strong>
                  <div className="kv-label" style={{ fontSize: "0.6rem" }}>
                    /{it.summary.total_members}
                  </div>
                </td>
                <td style={{ textAlign: "right" }}>
                  <div className="flex items-center gap-2" style={{ justifyContent: "flex-end" }}>
                    <button
                      className="btn-outline-gold"
                      onClick={() => navigate(`/attendance/${it.event.id}`)}
                      data-testid={`view-attendance-${it.event.id}`}
                    >
                      {t("attendance.viewDetails")}
                    </button>
                    {isAdmin && (
                      <button
                        type="button"
                        className="btn-danger flex items-center gap-1"
                        onClick={() => removeEvent(it.event)}
                        data-testid={`delete-attendance-${it.event.id}`}
                        title={t("common.delete", "Șterge")}
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function EventAttendanceDetail({ eventId, t, locale, isAdmin }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [excuseFor, setExcuseFor] = useState(null);
  const [excuseText, setExcuseText] = useState("");
  const [showVisitorForm, setShowVisitorForm] = useState(false);
  const [visitorForm, setVisitorForm] = useState({ first_name: "", last_name: "", grade: "", home_lodge_name: "" });
  const [visitorSaving, setVisitorSaving] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  const openQr = async () => {
    setQrLoading(true);
    try {
      const { data: qd } = await api.get(`/events/${eventId}/checkin-qr`);
      setQrData(qd);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setQrLoading(false);
    }
  };

  const load = async () => {
    try {
      const res = await api.get(`/events/${eventId}/attendance`);
      setData(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const setStatus = async (member_id, status, excuseNote = null) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/attendance/${member_id}`, {
        status,
        excuse_note: excuseNote,
      });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const bulkSet = async (status) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      await api.post(`/events/${eventId}/attendance/bulk`, { status, scope: "all" });
      await load();
      toast.success(t("common.save"));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitExcuse = async (e) => {
    e.preventDefault();
    if (!excuseFor) return;
    await setStatus(excuseFor.member_id, "excused", excuseText);
    setExcuseFor(null);
    setExcuseText("");
  };

  const submitVisitor = async (e) => {
    e.preventDefault();
    if (!visitorForm.first_name.trim() || !visitorForm.last_name.trim()) {
      toast.error(t("attendance.visitorNameRequired", "Numele și prenumele sunt obligatorii."));
      return;
    }
    setVisitorSaving(true);
    try {
      await api.post(`/events/${eventId}/visitors`, visitorForm);
      toast.success(t("attendance.visitorAdded", "Vizitator adăugat."));
      setVisitorForm({ first_name: "", last_name: "", grade: "", home_lodge_name: "" });
      setShowVisitorForm(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setVisitorSaving(false);
    }
  };

  const removeManualVisitor = async (visitor_member_id) => {
    if (!window.confirm(t("attendance.removeVisitorConfirm", "Ștergi acest vizitator?"))) return;
    try {
      await api.delete(`/events/${eventId}/visitors/${visitor_member_id}`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportCsv = () => {
    downloadUrl(`${API}/events/${eventId}/attendance/export.csv${authQuery()}`, `prezenta_${eventId}.csv`);
  };
  const exportPdf = () => {
    downloadUrl(`${API}/events/${eventId}/attendance/export.pdf${authQuery()}`, `prezenta_${eventId}.pdf`);
  };

  const summary = data?.summary;

  return (
    <div data-testid="attendance-detail-page">
      <button className="btn-outline-gold mb-4 flex items-center gap-1" onClick={() => navigate("/attendance")}>
        <ArrowLeft size={12} /> {t("attendance.title")}
      </button>
      {error && <div className="error-banner mb-4">{error}</div>}
      {data && (
        <>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <h1 className="page-title">{data.event.title}</h1>
              <p className="page-subtitle">
                {new Date(data.event.date).toLocaleString(locale, {
                  weekday: "long",
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {data.event.location ? ` · ${data.event.location}` : ""}
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {isAdmin && (
                <>
                  <button
                    className="btn-primary-gold flex items-center gap-1"
                    onClick={openQr}
                    disabled={qrLoading}
                    data-testid="attendance-qr-btn"
                  >
                    <QrCode size={12} /> {qrLoading ? "..." : t("attendance.qrCheckin", "QR PREZENȚĂ")}
                  </button>
                  <button
                    className="btn-outline-gold flex items-center gap-1"
                    onClick={() => bulkSet("present")}
                    disabled={busy}
                    data-testid="bulk-mark-present"
                  >
                    <CheckCircle2 size={12} /> {t("attendance.markAllPresent")}
                  </button>
                  <button
                    className="btn-outline-gold flex items-center gap-1"
                    onClick={() => bulkSet("absent")}
                    disabled={busy}
                    data-testid="bulk-mark-absent"
                  >
                    <XCircle size={12} /> {t("attendance.markAllAbsent")}
                  </button>
                </>
              )}
              <button className="btn-outline-gold flex items-center gap-1" onClick={exportCsv} data-testid="attendance-export-csv">
                <Download size={12} /> {t("attendance.exportCsv")}
              </button>
              <button className="btn-outline-gold flex items-center gap-1" onClick={exportPdf} data-testid="attendance-export-pdf">
                <FileTextIcon size={12} /> {t("attendance.exportPdf")}
              </button>
            </div>
          </div>

          <div className="gold-divider my-6" />

          {summary && (
            <div className="panel mb-6" data-testid="attendance-summary">
              <div className="panel-body" style={{ display: "flex", gap: "1.4rem", flexWrap: "wrap", alignItems: "center" }}>
                <div>
                  <div className="kv-label">{t("attendance.totalMembers")}</div>
                  <div className="tile-value" style={{ fontSize: "1.5rem" }}>{summary.total_members}</div>
                </div>
                <div>
                  <div className="kv-label">{t("attendance.present")}</div>
                  <div className="tile-value" style={{ fontSize: "1.5rem", color: "#3e6a3c" }}>{summary.present}</div>
                </div>
                <div>
                  <div className="kv-label">{t("attendance.absent")}</div>
                  <div className="tile-value" style={{ fontSize: "1.5rem", color: "#7c2a2a" }}>{summary.absent}</div>
                </div>
                <div>
                  <div className="kv-label">{t("attendance.excused")}</div>
                  <div className="tile-value" style={{ fontSize: "1.5rem", color: "#9a7530" }}>{summary.excused}</div>
                </div>
                <div>
                  <div className="kv-label">{t("attendance.confirmed", "Confirmat")}</div>
                  <div className="tile-value" style={{ fontSize: "1.5rem", color: "#9a7530" }} data-testid="attendance-confirmed-tile">{summary.confirmed || 0}</div>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <div className="kv-label">{t("attendance.percentage")}</div>
                  <div className="tile-value" style={{ fontSize: "1.8rem", color: "#0d1b2a" }}>{summary.percentage}%</div>
                </div>
              </div>
            </div>
          )}

          <table className="lodge-table" data-testid="attendance-rows">
            <thead>
              <tr>
                <th>{t("members.name", "Nume")}</th>
                <th>{t("members.grade", "Grad")}</th>
                <th style={{ textAlign: "center" }}>Status</th>
                <th style={{ textAlign: "center" }}>{t("attendance.method")}</th>
                <th style={{ textAlign: "center" }}>{t("attendance.recordedAt")}</th>
                <th>{t("attendance.excuseLabel", "Notă")}</th>
                {isAdmin && <th style={{ textAlign: "right" }}>{t("common.actions")}</th>}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.member_id} data-testid={`attendance-row-${r.member_id}`}>
                  <td>
                    {r.last_name} {r.first_name}
                    {r.is_invited && (
                      <span className="kv-label" style={{ marginLeft: 6, fontSize: "0.6rem" }}>
                        · {t("events.invitedMembers", "invitat")}
                      </span>
                    )}
                  </td>
                  <td>{r.grade || "—"}</td>
                  <td style={{ textAlign: "center" }}>
                    <StatusBadge status={r.status} t={t} />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <MethodBadge method={r.method} t={t} />
                  </td>
                  <td style={{ textAlign: "center", fontSize: "0.85rem" }}>
                    {r.recorded_at
                      ? new Date(r.recorded_at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
                      : "—"}
                  </td>
                  <td style={{ fontSize: "0.85rem", maxWidth: 240 }}>
                    {r.excuse_note ? (
                      <span style={{ color: "#9a7530", fontStyle: "italic" }}>{r.excuse_note}</span>
                    ) : (
                      <span className="kv-label">—</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td style={{ textAlign: "right" }}>
                      <div className="flex justify-end gap-1 flex-wrap">
                        <button
                          className="btn-outline-gold"
                          onClick={() => setStatus(r.member_id, "present")}
                          disabled={busy}
                          title={t("attendance.present")}
                          data-testid={`set-present-${r.member_id}`}
                        >
                          <CheckCircle2 size={11} />
                        </button>
                        <button
                          className="btn-outline-gold"
                          onClick={() => setStatus(r.member_id, "absent")}
                          disabled={busy}
                          title={t("attendance.absent")}
                          data-testid={`set-absent-${r.member_id}`}
                        >
                          <XCircle size={11} />
                        </button>
                        <button
                          className="btn-outline-gold"
                          onClick={() => {
                            setExcuseFor(r);
                            setExcuseText(r.excuse_note || "");
                          }}
                          title={t("attendance.addExcuse")}
                          data-testid={`add-excuse-${r.member_id}`}
                        >
                          <Pencil size={11} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Visitors section (cross-lodge check-ins) */}
          <div className="mt-8">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="section-heading" style={{ margin: 0 }}>{t("attendance.visitorsHeading")}</h3>
              {isAdmin && (
                <button
                  type="button"
                  className="btn-outline-gold flex items-center gap-1"
                  onClick={() => setShowVisitorForm(true)}
                  data-testid="add-manual-visitor-btn"
                >
                  + {t("attendance.addManualVisitor", "ADAUGĂ VIZITATOR MANUAL")}
                </button>
              )}
            </div>
            {(!data.visitors || data.visitors.length === 0) ? (
              <p style={{ fontStyle: "italic", color: "#858481", fontSize: "0.85rem" }} data-testid="no-visitors">
                {t("attendance.noVisitors")}
              </p>
            ) : (
              <table className="lodge-table" data-testid="visitors-table">
                <thead>
                  <tr>
                    <th>{t("members.name", "Nume")}</th>
                    <th>{t("members.grade", "Grad")}</th>
                    <th>{t("attendance.visitorFrom")}</th>
                    <th style={{ textAlign: "center" }}>{t("attendance.method")}</th>
                    <th style={{ textAlign: "center" }}>{t("attendance.recordedAt")}</th>
                    {isAdmin && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {data.visitors.map((v) => (
                    <tr key={v.member_id} data-testid={`visitor-${v.member_id}`}>
                      <td>
                        {v.last_name} {v.first_name}
                        {v.is_manual && (
                          <span className="kv-label" style={{ marginLeft: 6, fontSize: "0.6rem", color: "#9a7530" }}>
                            {t("attendance.visitorManualBadge", "(manual)")}
                          </span>
                        )}
                      </td>
                      <td>{v.grade || "—"}</td>
                      <td style={{ fontFamily: "Cinzel, serif", fontSize: "0.85rem" }}>{v.home_lodge_name || "—"}</td>
                      <td style={{ textAlign: "center" }}>
                        <MethodBadge method={v.method} t={t} />
                      </td>
                      <td style={{ textAlign: "center", fontSize: "0.85rem" }}>
                        {v.recorded_at ? new Date(v.recorded_at).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" }) : "—"}
                      </td>
                      {isAdmin && (
                        <td style={{ textAlign: "right" }}>
                          {v.is_manual && (
                            <button
                              className="btn-outline-gold flex items-center gap-1"
                              onClick={() => removeManualVisitor(v.member_id)}
                              data-testid={`remove-visitor-${v.member_id}`}
                              title={t("common.delete")}
                            >
                              <Trash2 size={11} />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {qrData && (
        <div className="dialog-overlay" onClick={() => setQrData(null)}>
          <div
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 460, textAlign: "center" }}
            data-testid="checkin-qr-modal"
          >
            <h2 className="dialog-title" style={{ marginBottom: 4 }}>
              {t("attendance.qrCheckinTitle", "Prezență prin scanare QR")}
            </h2>
            <p className="page-subtitle" style={{ marginBottom: 12 }}>
              {qrData.event?.title}
            </p>
            <img
              src={qrData.png_data_url}
              alt="QR check-in"
              style={{ width: 320, maxWidth: "100%", margin: "0 auto", border: "1px solid #cfc9b7", borderRadius: 8 }}
              data-testid="checkin-qr-image"
            />
            <p style={{ fontSize: "0.8rem", color: "#6b6557", marginTop: 12, lineHeight: 1.5 }}>
              {t(
                "attendance.qrCheckinHint",
                "Afișează acest cod la intrarea în Templu. Frații îl scanează din aplicația mobilă (Astăzi → Scanează prezența) și sunt marcați prezenți automat."
              )}
            </p>
            <div className="flex justify-center gap-3 mt-5">
              <button type="button" className="btn-outline-gold" onClick={() => window.print()} data-testid="checkin-qr-print">
                {t("attendance.qrPrint", "TIPĂREȘTE")}
              </button>
              <button type="button" className="btn-primary-gold" onClick={() => setQrData(null)}>
                {t("common.close", "ÎNCHIDE")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showVisitorForm && (
        <div className="dialog-overlay" onClick={() => setShowVisitorForm(false)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitVisitor}
            style={{ maxWidth: 480 }}
            data-testid="manual-visitor-form"
          >
            <h2 className="dialog-title">
              {t("attendance.addManualVisitorTitle", "Adaugă vizitator manual")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <div>
                <label className="label-lodge">{t("members.lastName", "Nume")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={visitorForm.last_name}
                  onChange={(e) => setVisitorForm({ ...visitorForm, last_name: e.target.value })}
                  data-testid="visitor-last-name"
                />
              </div>
              <div>
                <label className="label-lodge">{t("members.firstName", "Prenume")} *</label>
                <input
                  className="input-lodge"
                  required
                  value={visitorForm.first_name}
                  onChange={(e) => setVisitorForm({ ...visitorForm, first_name: e.target.value })}
                  data-testid="visitor-first-name"
                />
              </div>
              <div>
                <label className="label-lodge">{t("members.grade", "Grad")}</label>
                <input
                  className="input-lodge"
                  value={visitorForm.grade}
                  onChange={(e) => setVisitorForm({ ...visitorForm, grade: e.target.value })}
                  data-testid="visitor-grade"
                  placeholder="Maestru / Companion / Ucenic"
                />
              </div>
              <div>
                <label className="label-lodge">{t("attendance.visitorFrom", "Loja de proveniență")}</label>
                <input
                  className="input-lodge"
                  value={visitorForm.home_lodge_name}
                  onChange={(e) => setVisitorForm({ ...visitorForm, home_lodge_name: e.target.value })}
                  data-testid="visitor-home-lodge"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button type="button" className="btn-outline-gold" onClick={() => setShowVisitorForm(false)}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold" disabled={visitorSaving} data-testid="visitor-save-btn">
                {visitorSaving ? "..." : t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}

      {excuseFor && (
        <div className="dialog-overlay" onClick={() => setExcuseFor(null)}>
          <form
            className="dialog-panel"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitExcuse}
            data-testid="excuse-form"
            style={{ maxWidth: 480 }}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="dialog-title" style={{ margin: 0 }}>
                {t("attendance.excuseTitle")} · {excuseFor.last_name} {excuseFor.first_name}
              </h2>
              <button type="button" onClick={() => setExcuseFor(null)}>
                <AlertCircle size={20} />
              </button>
            </div>
            <div>
              <label className="label-lodge">{t("attendance.excuseLabel")}</label>
              <textarea
                rows={4}
                className="input-lodge"
                value={excuseText}
                onChange={(e) => setExcuseText(e.target.value)}
                placeholder={t("attendance.excusePlaceholder")}
                data-testid="excuse-textarea"
              />
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button type="button" className="btn-outline-gold" onClick={() => setExcuseFor(null)}>
                {t("common.cancel")}
              </button>
              <button type="submit" className="btn-primary-gold" data-testid="excuse-save">
                {t("common.save")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
